import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import { useTranslation } from "react-i18next";
import { auth } from "../../services/api";

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [step, setStep] = useState("email"); // email → code → done
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await auth.forgotPassword(email);
      setStep("code");
    } catch (err) {
      setError(err.message || t("forgot.sendFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 6) {
      setError(t("auth.passwordMin"));
      return;
    }
    setSubmitting(true);
    try {
      await auth.resetPassword(email, code, newPassword);
      setStep("done");
    } catch (err) {
      setError(err.message || err.error || t("forgot.resetFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-th-bg">
    <div className="min-h-full flex flex-col items-center justify-center px-6 py-8">
      <img
        src={theme === "dark" ? "/pinned-logo-light-stacked.png" : "/pinned-logo-dark-stacked.png"}
        alt={t("app.title")}
        className="w-64 mb-8"
      />

      {step === "email" && (
        <form onSubmit={handleRequestCode} className="w-full max-w-sm space-y-3">
          <p className="text-sm text-th-muted text-center mb-2">
            {t("forgot.enterEmail")}
          </p>
          {error && (
            <p className="text-sm text-th-danger bg-th-danger-bg border border-th-danger-border px-3 py-2 rounded-xl">
              {error}
            </p>
          )}
          <input
            type="email"
            placeholder={t("auth.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-3.5 bg-th-surface border border-th-border rounded-xl text-base text-th-text placeholder-th-muted focus:outline-none focus:border-th-accent-border focus:ring-1 focus:ring-th-accent-border transition-all"
          />
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3.5 bg-th-accent text-th-bg rounded-xl text-sm font-semibold disabled:opacity-50"
          >
            {submitting ? t("forgot.sending") : t("forgot.sendCode")}
          </button>
          <p className="text-sm text-th-muted text-center mt-4">
            <Link to="/login" className="text-th-accent font-medium">
              {t("forgot.backToLogin")}
            </Link>
          </p>
        </form>
      )}

      {step === "code" && (
        <form onSubmit={handleResetPassword} className="w-full max-w-sm space-y-3">
          <p className="text-sm text-th-muted text-center mb-2">
            {t("forgot.codeSent")} <strong className="text-th-text">{email}</strong>
          </p>
          {error && (
            <p className="text-sm text-th-danger bg-th-danger-bg border border-th-danger-border px-3 py-2 rounded-xl">
              {error}
            </p>
          )}
          <input
            type="text"
            inputMode="numeric"
            placeholder={t("forgot.enterCode")}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            maxLength={6}
            className="w-full px-4 py-3.5 bg-th-surface border border-th-border rounded-xl text-base text-th-text placeholder-th-muted focus:outline-none focus:border-th-accent-border focus:ring-1 focus:ring-th-accent-border transition-all text-center tracking-[0.5em] font-mono"
          />
          <input
            type="password"
            placeholder={t("forgot.newPassword")}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            className="w-full px-4 py-3.5 bg-th-surface border border-th-border rounded-xl text-base text-th-text placeholder-th-muted focus:outline-none focus:border-th-accent-border focus:ring-1 focus:ring-th-accent-border transition-all"
          />
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3.5 bg-th-accent text-th-bg rounded-xl text-sm font-semibold disabled:opacity-50"
          >
            {submitting ? t("forgot.resetting") : t("forgot.resetPassword")}
          </button>
          <p className="text-sm text-th-muted text-center">
            {t("verify.codeExpiry")}
          </p>
        </form>
      )}

      {step === "done" && (
        <div className="w-full max-w-sm text-center space-y-4">
          <p className="text-lg font-semibold text-th-text">{t("forgot.success")}</p>
          <p className="text-sm text-th-muted">{t("forgot.successMessage")}</p>
          <button
            onClick={() => navigate("/login")}
            className="w-full py-3.5 bg-th-accent text-th-bg rounded-xl text-sm font-semibold"
          >
            {t("auth.login")}
          </button>
        </div>
      )}
    </div>
    </div>
  );
}

export default ForgotPassword;
